import { Input, Spin, Table, Tag } from 'antd';
import EntityLogo from 'components/EntityLogo';
import useEntities from 'hooks/useEntities';
import useIsMobile from 'hooks/useIsMobile';
import useFetchEntities from 'hooks/useFetchEntities';
import { useEffect, useMemo, useState } from 'react';
import { removeAccents } from 'utils/oneLiners';

const SeasonSelectionTable = ({
  open,
  filters: extraFilters = {},
  selectedRowKeys = [],
  onChange,
  style,
  className,
  header = null,
  ...tableProps
}) => {
  // Season data loading
  const { data: seasonsRaw, isLoading: seasonsLoading } = useFetchEntities(
    open ? 'Season' : null,
    {
      filters: {
        status: ['OPEN_FOR_REGISTRATION', 'RUNNING'],
        playerRegMandatory: true,
        ...extraFilters
      },
      relations: 'Competition'
    }
  );
  const entities = useEntities();
  const entity = entities.Season;
  const [search, setSearch] = useState('');
  const [filteredSeasons, setFilteredSeasons] = useState([]);
  const isMobile = useIsMobile();

  // Filter seasons based on search
  useEffect(() => {
    if (!seasonsRaw) {
      setFilteredSeasons([]);
      return;
    }

    let filtered = seasonsRaw;

    // Apply search filter
    if (search.trim() !== '') {
      const searchWords = removeAccents(search.trim().toUpperCase()).split(' ').map(w => w.trim());

      filtered = filtered.filter(season => {
        // Create search index for each season
        const searchIndex = removeAccents([
          season.name || '',
          season.nick || '',
          season.short || '',
          season.Competition?.name || ''
        ].join(' ').toUpperCase());

        // Check if all search words are found in the search index
        for (const searchWord of searchWords) {
          if (searchIndex.indexOf(searchWord) === -1) {
            return false;
          }
        }
        return true;
      });
    }

    setFilteredSeasons(filtered);
  }, [seasonsRaw, search]);

  function renderMobileRow(record) {
    return (
      <div className="flex flex-col gap-2">
        <div className="text-sm text-gray-600 flex flex-wrap gap-2 items-center">
          {entity.columns['Competition.Name']?.render(record)}
          <span>•</span>
          {entity.columns['Name_Mobile']?.render(record)}
          <span className="ml-1" />
          {entity.columns['ParticipationFee']?.render(record)}
        </div>
      </div>
    )
  }

  const columns = useMemo(() => {
    if (isMobile) {
      return [
        {
          title: 'Season',
          render: (record) => {
            return (
              renderMobileRow(record)
            )
          }
        }
      ]
    } else {
      return [
        {
          title: 'Competition',
          dataIndex: ['Competition', 'name'],
          key: 'competition',
          render: (_, rec) => (
            <div className="flex items-center gap-2">
              <EntityLogo entity={rec.Competition} size={24} />
              <span>{rec.Competition?.name || '-'}</span>
            </div>
          ),
        },
        {
          title: 'Season name',
          dataIndex: 'name',
          key: 'name',
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          render: (status) => {
            const statusMap = {
              'OPEN_FOR_REGISTRATION': 'Open for Registration',
              'RUNNING': 'Running',
              'FINISHED': 'Finished',
              'NOTSTARTED': 'Not Started',
              'POSTPONED': 'Postponed',
              'CANCELLED': 'Cancelled'
            };
            return statusMap[status] || status;
          },
        },
        entity.columns['ParticipationFee'],
        {
          title: 'Start Date',
          dataIndex: 'startDate',
          key: 'startDate',
          render: (date) => date ? new Date(date).toLocaleDateString() : '-',
        },
        {
          title: 'End Date',
          dataIndex: 'endDate',
          key: 'endDate',
          render: (date) => date ? new Date(date).toLocaleDateString() : '-',
        },
      ];
    }
  }, []);

  // Memoized sorted data
  const sortedSeasons = useMemo(() => {
    return filteredSeasons;
  }, [filteredSeasons]);

  return (
    <div style={style} className={className}>
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <div>
            {header}
          </div>
          <div className="ml-auto">
            {/* <span className="whitespace-nowrap mr-2">
              Seasons: <span className="font-bold">{filteredSeasons?.length?.toLocaleString() ?? 0}</span>
            </span>
            <Input
              className="no-hightlight-on-focus"
              placeholder="Search seasons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear={true}
              size="small"
              style={{ width: 180 }}
            /> */}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, marginBottom: 8 }}>
        {seasonsLoading ? (
          <div className="flex items-center justify-center h-full"><Spin /></div>
        ) : (
          <Table
            rowKey="seasonId"
            columns={columns}
            scroll={{ y: 'calc(60vh - 120px)' }}
            dataSource={sortedSeasons}
            rowSelection={{
              selectedRowKeys,
              onChange,
              type: 'radio',
            }}
            onRow={(record) => ({
              onClick: () => {
                const newSelection = selectedRowKeys.includes(record.seasonId) ? [] : [record.seasonId];
                onChange && onChange(newSelection);
              },
              style: { cursor: 'pointer' }
            })}
            pagination={false}
            size="small"
            sticky={{ offsetHeader: 0 }}
            {...tableProps}
          />
        )}
      </div>
    </div>
  );
};

export default SeasonSelectionTable; 